import onboardingMessages from '@/messages/onboarding';
import downloadFile from '@/utils/downloadFile';
import { Box, Button, Center, HStack, Image, Text, VStack } from '@chakra-ui/react';
import React, { useEffect, useRef } from 'react';
import { parse as parseCSV } from 'papaparse';
import { TParsedRosterList } from '@/store/useOrganizationOnboardingStore';
import { TRosterItemPerson } from '@/types/Roster.types';
import { MAX_LINES_IN_ROSTER } from '@/constants';
import FilePicker from '@/components/atoms/FilePicker';
import iconsPng from '@/assets/img/png/icons';
import RosterManualEntryForm, { RosterManualEntryFormProps } from './C_RosterManualEntryForm';
import { TUserAffiliation } from '@/types/User.types';

const MAX_WIDTH = '610px';

const trimRosterValue = (value: string | undefined) => value?.trim() ?? '';

interface Props extends RosterManualEntryFormProps {
  csvTabStep: 0 | 1;
  selectedFile: File | null;
  setSelectedFile: (newFile: File | null) => void;
  preview: string | null;
  setPreview: (newPreview: string | null) => void;
  setRoster: (roster: TParsedRosterList) => void;
  setCustomRosterError: (error: string) => void;
  titleText?: string;
  descriptionText?: string;
  buttonsColor?: 'primary' | 'secondary';
}

const RosterCSVFile = ({
  csvTabStep,
  selectedFile,
  setSelectedFile,
  setCustomRosterError,
  setRoster,
  preview,
  setPreview,
  methods,
  peerSupportData,
  setPeerSupportData,
  titleText = onboardingMessages.uploadYourCompletedRoster,
  descriptionText = onboardingMessages.uploadYourRosterDescription,
  buttonsColor = 'secondary',
}: Props) => {
  const csvFileRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (selectedFile) {
      parseCSV(selectedFile, {
        header: true,
        skipEmptyLines: true,
        complete: (results: { data: TRosterItemPerson[] }) => {
          if (results.data.length > MAX_LINES_IN_ROSTER) {
            setCustomRosterError(onboardingMessages.tooManyRecordsInRosterFile);
          } else {
            try {
              setCustomRosterError('');
              setRoster(
                (results.data as TRosterItemPerson[]).map((rosterItemPerson) => ({
                  ...rosterItemPerson,
                  'First Name': trimRosterValue(rosterItemPerson['First Name']),
                  'Last Name': trimRosterValue(rosterItemPerson['Last Name']),
                  Phone: trimRosterValue(rosterItemPerson.Phone),
                  Email: trimRosterValue(rosterItemPerson.Email).toLowerCase(),
                  Affiliation: trimRosterValue(rosterItemPerson.Affiliation).toLowerCase() as TUserAffiliation,
                  'Peer Support': false, // super important! as we do not take in peer support data from the roster file
                })),
              );
              setPreview(selectedFile?.name);
            } catch (_) {
              setCustomRosterError('Something went wrong. Please check your roster file and upload it again.');
            }
          }
        },
      });
    }
  }, [selectedFile]);

  const triggerFilePicker = () => {
    if (csvFileRef.current) {
      csvFileRef.current.click();
    }
  };

  if (csvTabStep === 1) {
    return (
      <RosterManualEntryForm
        methods={methods}
        peerSupportData={peerSupportData}
        setPeerSupportData={setPeerSupportData}
      />
    );
  }

  return (
    // TODO: max 610px
    <VStack spacing={'50px'} marginTop={'40px'}>
      <VStack marginTop={'10px'}>
        <Text fontSize={'20px'} color={'text.darkBlue'} lineHeight={'24px'} fontWeight={'600'}>
          {onboardingMessages.createYourRosterCSVSubtitle}
        </Text>
        <Text color={'text.mediumGray'} lineHeight={'20px'} fontWeight={'500'}>
          {onboardingMessages.createYourRosterCSVSubtitleDescription}
        </Text>
      </VStack>
      <PreviewSection
        title={onboardingMessages.downloadOurRosterTemplate}
        description={onboardingMessages.downloadRosterTemplateDescription}
        number={'1'}
        buttonLabel={onboardingMessages.downloadRosterTemplate}
        onButtonPress={() => {
          downloadFile('/files/roster-template.csv');
        }}
        buttonsColor={buttonsColor}
      />
      <VStack>
        <PreviewSection
          title={titleText}
          description={descriptionText}
          number={'2'}
          buttonLabel={onboardingMessages.uploadCSV}
          onButtonPress={triggerFilePicker}
          isButtonDisabled={preview !== null && preview !== ''}
          buttonsColor={buttonsColor}
        />

        {preview && (
          <HStack
            width={'295px'}
            marginTop={'20px'}
            borderRadius={'8px'}
            background={'white'}
            padding={'18px'}
            justify={'space-between'}
          >
            <Text color={'text.darkBlue'} fontSize={'14px'} fontWeight={'500'} noOfLines={1} marginRight={'18px'}>
              {preview}
            </Text>
            <Box
              onClick={() => {
                setSelectedFile(null);
                if (csvFileRef.current) csvFileRef.current.value = '';
                setPreview(null);
              }}
              cursor={'pointer'}
            >
              <Image alt="delete-csv-x" src={iconsPng.redX} width={25} height={25} />
            </Box>
          </HStack>
        )}
      </VStack>

      <FilePicker
        inputRef={csvFileRef}
        selectedFile={selectedFile}
        setSelectedFile={setSelectedFile}
        setPreview={(newPreview) => setPreview(newPreview)}
        accept={'.csv'}
      />
    </VStack>
  );
};

export default RosterCSVFile;

const PreviewSection = ({
  title,
  description,
  number,
  buttonLabel,
  onButtonPress,
  isButtonDisabled = false,
  buttonsColor = 'secondary',
}: {
  title: string;
  description: string;
  number: string;
  buttonLabel: string;
  onButtonPress: () => void;
  isButtonDisabled?: boolean;
  buttonsColor?: 'primary' | 'secondary';
}) => {
  return (
    <VStack>
      <HStack alignItems={'flex-start'} gap={0}>
        <VStack paddingX={'16px'}>
          <HStack gap={'16px'}>
            <Center background={`${buttonsColor}.500`} borderRadius={'full'} width={'30px'} height={'30px'}>
              <Text fontWeight={900} color={'white'} lineHeight={'20px'} fontSize={'16px'}>
                {number}
              </Text>
            </Center>
            <Text fontWeight={600} fontSize={'26px'} lineHeight={'32px'}>
              {title}
            </Text>
          </HStack>
          <Text
            fontSize={'16px'}
            maxWidth={MAX_WIDTH}
            textAlign={'center'}
            lineHeight={'20px'}
            marginTop={'16px'}
            color={'text.mediumBlue'}
            fontWeight={400}
          >
            {description}
          </Text>
        </VStack>
      </HStack>
      <Button
        marginTop={'20px'}
        variant={'formSubmit'}
        onClick={onButtonPress}
        disabled={isButtonDisabled}
        isDisabled={isButtonDisabled}
        backgroundColor={'secondary.500'}
        _hover={{ backgroundColor: isButtonDisabled ? `secondary.400` : `secondary.600` }}
      >
        {buttonLabel}
      </Button>
    </VStack>
  );
};
